import { API } from '../api/API';
import useAuthStore from '../store/authStore';

/**
 * @typedef {Object} Consultation
 * @property {number} id - ID auto-généré de la consultation
 * @property {string} label - Label de la consultation
 * @property {string} description - Description de la consultation
 * @property {number} partenaire_id - ID du partenaire
 * @property {number} prix - Prix de la consultation
 * @property {('clinique'|'clinique_labo')} type - Type de consultation
 * @property {boolean} status - Statut de la consultation (actif/inactif)
 */

const consultationService = {
  // Récupérer toutes les consultations
  getAllConsultations: async (page = 1, limit = 10, filters = {}) => {
    try {
      const store = useAuthStore.getState();
      const params = {
        page,
        limit,
        ...filters,
        partenaire_id: store.user?.partie_id,
      };
      
      const response = await API.get('/consultations', { params });
      return {
        success: true,
        data: response.data.data,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des consultations:', error);
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération des consultations',
        details: error.response?.data?.details
      };
    }
  },

  // Créer une nouvelle consultation
  createConsultation: async (consultationData) => {
    try {
      const store = useAuthStore.getState();
      const data = {
        ...consultationData,
        partenaire_id: store.user?.partie_id
      };
      
      const response = await API.post('/consultations', data);
      return {
        success: true,
        data: response.data,
        message: 'Consultation créée avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la création de la consultation',
        details: error.response?.data?.details
      };
    }
  },

  // Récupérer une consultation par ID
  getConsultationById: async (id) => {
    try {
      const response = await API.get(`/consultations/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la récupération de la consultation',
        details: error.response?.data?.details
      };
    }
  },

  // Mettre à jour une consultation
  updateConsultation: async (id, updateData) => {
    try {
      const response = await API.put(`/consultations/${id}`, updateData);
      return {
        success: true,
        data: response.data,
        message: 'Consultation mise à jour avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la mise à jour de la consultation',
        details: error.response?.data?.details
      };
    }
  },

  // Supprimer une consultation
  deleteConsultation: async (id) => {
    try {
      await API.delete(`/consultations/${id}`);
      return {
        success: true,
        message: 'Consultation supprimée avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la suppression de la consultation',
        details: error.response?.data?.details
      };
    }
  },

  // Activer ou désactiver une consultation
  toggleConsultationStatus: async (id) => {
    try {
      const response = await API.patch(`/consultations/${id}/toggle-status`);
      return {
        success: true,
        data: response.data,
        message: 'Statut de la consultation modifié avec succès'
      };
    } catch (error) {
      throw {
        success: false,
        message: error.response?.data?.error || 'Erreur lors de la modification du statut',
        details: error.response?.data?.details
      };
    }
  },

  // Créer une consultation avec réception
  createConsultationWithReception: async (data) => {
    try {
      const store = useAuthStore.getState();
      const consultationData = {
        ...data,
        partenaire_id: store.user?.partie_id
      };
      const response = await API.post('/consultations/avec-reception', consultationData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la création de la consultation avec réception:', error);
      throw error;
    }
  },

  // Récupérer les consultations d'un assuré
  getConsultationsForAssure: async (assure_id) => {
    try {
      const response = await API.get(`/consultations/assure/${assure_id}`);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des consultations de l\'assuré:', error);
      throw error;
    }
  }
};

export default consultationService;